import React, { useState, useEffect } from 'react';
import { useCollegePrepContext } from '../../../contexts/CollegePrepContext';

const AIAnalysis = ({ responses, onAnalysisComplete }) => {
  const { addProgram } = useCollegePrepContext();
  const [analysisState, setAnalysisState] = useState('ready'); // 'ready', 'analyzing', 'complete'
  const [analysis, setAnalysis] = useState(null);

  const completedResponses = Object.entries(responses).filter(
    ([key, value]) => value && value.trim().length > 0
  );

  const canRunAnalysis = completedResponses.length >= 8; // Need at least 8 responses for meaningful analysis

  const runAnalysis = async () => {
    if (!canRunAnalysis) return;

    setAnalysisState('analyzing');

    // Simulate AI analysis processing time
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Mock AI analysis results - in real implementation, this would call an AI service
    const mockAnalysis = {
      workStyle: ['Independent researcher', 'Problem-solver', 'Detail-oriented'],
      values: ['Social impact', 'Learning and growth', 'Helping others'],
      strengths: ['Analytical thinking', 'Persistence', 'Communication'],
      environment: ['Collaborative but focused', 'Research-oriented', 'Technology-enabled'],
      motivations: ['Making a difference', 'Intellectual challenge', 'Skill development'],
      programSuggestions: [
        {
          name: 'Research Science Institute (RSI)',
          organization: 'MIT',
          matchReason: 'Perfect for independent research and analytical thinking',
          confidence: 95,
          category: 'research'
        },
        {
          name: 'NC Governor\'s School',
          organization: 'NC State University',
          matchReason: 'Matches your collaborative style and focus on social impact',
          confidence: 88,
          category: 'academic'
        },
        {
          name: 'Duke TIP Summer Studies',
          organization: 'Duke',
          matchReason: 'Great environment for learning and growth',
          confidence: 82,
          category: 'academic'
        }
      ]
    };

    setAnalysis(mockAnalysis);
    setAnalysisState('complete');
    
    if (onAnalysisComplete) {
      onAnalysisComplete(mockAnalysis);
    }
  };

  useEffect(() => {
    if (analysisState === 'complete' && analysis) {
      // Auto-scroll to analysis results
      const element = document.getElementById('ai-analysis-results');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [analysisState, analysis]);

  if (!canRunAnalysis) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <div className="text-4xl mb-3">🤖</div>
        <h3 className="text-lg font-semibold text-amber-800 mb-2">
           Intelligent Analysis Coming Soon
        </h3>
        <p className="text-amber-700 mb-4">
          Complete at least 8 discovery questions to unlock personalized program suggestions.
        </p>
        <div className="text-sm text-amber-600">
          {completedResponses.length} of 8 minimum responses completed
        </div>
      </div>
    );
  }

  if (analysisState === 'ready') {
    return (
      <div className="bg-gradient-to-r from-azure-50 to-indigo-50 border border-azure-200 rounded-xl p-6 text-center">
        <div className="text-4xl mb-3">🧠</div>
        <h3 className="text-lg font-semibold text-azure-800 mb-2">
          Ready for Intelligent Analysis
        </h3>
        <p className="text-azure-700 mb-4">
          Great work! You've completed {completedResponses.length} discovery questions. 
          Ready to analyze your responses and get personalized summer program suggestions?
        </p>
        <button
          onClick={runAnalysis}
          className="px-6 py-3 bg-azure-600 text-white font-semibold rounded-lg hover:bg-azure-700 transition-colors"
        >
          🚀 Start Exploring Programs
        </button>
      </div>
    );
  }

  if (analysisState === 'analyzing') {
    return (
      <div className="bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-200 rounded-xl p-6 text-center">
        <div className="text-4xl mb-3 animate-pulse">🔮</div>
        <h3 className="text-lg font-semibold text-violet-800 mb-2">
          Analyzing Your Responses...
        </h3>
        <p className="text-violet-700 mb-4">
          Processing your discovery responses to find programs that match your unique interests and working style.
        </p>
        
        <div className="space-y-2">
          {[
            'Analyzing work style preferences...',
            'Identifying core values...',
            'Mapping natural strengths...',
            'Finding program matches...'
          ].map((step, index) => (
            <div key={index} className="flex items-center justify-center gap-2 text-sm text-violet-600">
              <div className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: `${index * 0.2}s` }} />
              {step}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (analysisState === 'complete' && analysis) {
    return (
      <div id="ai-analysis-results" className="space-y-6">
        {/* Analysis Summary */}
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-50 border border-emerald-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-2xl">🎯</div>
            <h3 className="text-xl font-semibold text-emerald-800">Your Interest Profile</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-emerald-700 mb-2">Work Style</h4>
              <div className="space-y-1">
                {analysis.workStyle.map((style, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-emerald-600">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                    {style}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-emerald-700 mb-2">Core Values</h4>
              <div className="space-y-1">
                {analysis.values.map((value, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-emerald-600">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                    {value}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-emerald-700 mb-2">Natural Strengths</h4>
              <div className="space-y-1">
                {analysis.strengths.map((strength, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-emerald-600">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                    {strength}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-emerald-700 mb-2">Ideal Environment</h4>
              <div className="space-y-1">
                {analysis.environment.map((env, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-emerald-600">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                    {env}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Program Suggestions */}
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-2xl">🎓</div>
            <h3 className="text-xl font-semibold text-slate-900">Recommended Programs</h3>
          </div>
          
          <div className="space-y-4">
            {analysis.programSuggestions.map((program, index) => (
              <div key={index} className="border border-stone-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-slate-900">{program.name}</h4>
                    <p className="text-sm text-stone-600">{program.organization}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-emerald-600">{program.confidence}% match</div>
                    <div className="w-16 bg-stone-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-500 h-2 rounded-full"
                        style={{ width: `${program.confidence}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-stone-700 mb-3">
                  <strong>Why this matches:</strong> {program.matchReason}
                </p>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleAddToCollection(program)}
                    className="px-3 py-1 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700"
                  >
                    Add to Collection
                  </button>
                  <button className="px-3 py-1 border border-stone-300 text-stone-700 text-sm font-medium rounded hover:bg-stone-50">
                    Tell me more
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-azure-50 border border-azure-200 rounded-xl p-6">
          <h3 className="font-semibold text-azure-800 mb-3">🎯 Next Steps</h3>
          <div className="space-y-2 text-sm text-azure-700">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-azure-500 rounded-full" />
              Add interesting programs to your collection for detailed research
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-azure-500 rounded-full" />
              Use the research tools to investigate application requirements
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-azure-500 rounded-full" />
              Check out the planning timeline to stay organized
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleAddToCollection = (program) => {
    const programData = {
      name: program.name,
      organization: program.organization,
      category: program.category || 'other',
      description: `AI-recommended program: ${program.matchReason}`,
      competitiveness: program.confidence > 90 ? 'ultra' : program.confidence > 80 ? 'high' : 'moderate',
      status: 'discovered',
      source: 'ai-recommendation',
      aiMatchConfidence: program.confidence,
      aiMatchReason: program.matchReason
    };
    
    addProgram(programData);
    
    // Show success feedback
    const button = event.target;
    const originalText = button.textContent;
    button.textContent = '✓ Added';
    button.disabled = true;
    button.classList.add('bg-emerald-600');
    button.classList.remove('bg-emerald-600', 'hover:bg-emerald-700');
    
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.classList.remove('bg-emerald-600');
      button.classList.add('bg-emerald-600', 'hover:bg-emerald-700');
    }, 2000);
  };

  return null;
};

export default AIAnalysis;